import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, MapPin } from 'lucide-react';
import { luxuryEase } from '../lib/motion';
import Photo from './Photo';

const statusStyles = {
  amber: 'bg-nosta-gold text-white',
  green: 'bg-emerald-800 text-white',
};

const PLAN_TAB = 'Kat ve Vaziyet Planları';

export default function ProjectModal({ project, onClose, onImageClick }) {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setActiveTab(0);
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [project.id, onClose]);

  const images = project.galleryTabs
    ? project.galleryTabs[activeTab].images
    : project.simpleGallery || [];

  const isPlanTab = (project.galleryTabs?.[activeTab]?.tabName || '') === PLAN_TAB;

  const getUrl = (img) => (typeof img === 'string' ? img : img.url);
  const getTitle = (img) => (typeof img === 'string' ? undefined : img.title);
  const getAlt = (img, fallback) => (typeof img === 'string' ? fallback : img.alt || fallback);

  const handleImageClick = (index) => {
    onImageClick(images.map(getUrl), index);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.45, ease: luxuryEase }}
      className="fixed inset-0 z-50 bg-black/80 overflow-y-auto backdrop-blur-[6px]"
      onClick={onClose}
    >
      <div className="min-h-screen py-4 sm:py-16 px-3 sm:px-4">
        <motion.div
          initial={{ y: 64, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.75, ease: luxuryEase }}
          className="max-w-6xl mx-auto bg-nosta-paper overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white text-nosta-ink p-2 transition-all"
              aria-label="Kapat"
            >
              <X size={22} />
            </button>

            <div className="relative h-[240px] sm:h-[380px] md:h-[440px] overflow-hidden">
              <motion.div
                className="h-full w-full"
                initial={{ scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.4, ease: luxuryEase }}
              >
                <Photo
                  src={project.coverImage}
                  alt={project.coverImageAlt || project.title}
                  priority
                  sizes="(min-width: 1152px) 1152px, 100vw"
                  className="w-full h-full"
                />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 md:p-10 text-white">
                <motion.span
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.6, ease: luxuryEase }}
                  className={`inline-block px-3 py-1 text-[11px] tracking-wideish uppercase mb-4 ${
                    statusStyles[project.statusColor]
                  }`}
                >
                  {project.status}
                </motion.span>
                <motion.h2
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.75, ease: luxuryEase }}
                  className="font-display text-3xl sm:text-4xl md:text-5xl mb-3"
                >
                  {project.title}
                </motion.h2>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="flex items-center gap-2 text-white/75"
                >
                  <MapPin size={16} className="text-nosta-goldSoft" />
                  <span>{project.location}</span>
                </motion.div>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-8 md:p-10">
            <p className="text-lg text-nosta-mute leading-relaxed mb-10 max-w-3xl">{project.description}</p>

            {project.videoId && (
              <div className="w-full mb-12">
                <div className="bg-nosta-ink overflow-hidden aspect-video">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${project.videoId}?rel=0&modestbranding=1&enablejsapi=1`}
                    title="Nosta Yapı Proje Tanıtımı"
                    style={{ border: 0 }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {project.galleryTabs && (
              <div className="mb-8 overflow-x-auto">
                <div className="flex gap-1 min-w-max border-b border-nosta-line">
                  {project.galleryTabs.map((tab, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTab(i)}
                      className={`pb-3 px-4 text-sm tracking-wide transition-all ${
                        activeTab === i
                          ? 'text-nosta-ink border-b-2 border-nosta-gold'
                          : 'text-nosta-mute hover:text-nosta-ink'
                      }`}
                    >
                      {tab.tabName}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div
              className={`grid grid-cols-1 ${
                isPlanTab ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'
              } gap-5`}
            >
              {images.map((img, i) => {
                const url = getUrl(img);
                const title = getTitle(img);
                const alt = getAlt(img, `${project.title} - ${i + 1}`);

                return (
                  <motion.div
                    key={`${activeTab}-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.05, ease: luxuryEase }}
                    whileHover={{ y: -4 }}
                    className="cursor-pointer"
                    onClick={() => handleImageClick(i)}
                  >
                    <div
                      className={`relative ${
                        isPlanTab ? 'h-80' : 'h-60'
                      } overflow-hidden bg-white border border-nosta-line`}
                    >
                      <Photo
                        src={url}
                        alt={alt}
                        contain={isPlanTab}
                        sizes={isPlanTab ? '(min-width: 640px) 50vw, 100vw' : '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'}
                        className={`w-full h-full ${isPlanTab ? 'p-4 bg-white' : ''}`}
                      />
                    </div>
                    {title && (
                      <h4 className="mt-3 text-center font-display text-xl text-nosta-ink">{title}</h4>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
